/**
 * ==========================================================================
 * IM8 HEALTH - COMPREHENSIVE ACCESSIBILITY FIXES (JavaScript)
 * ==========================================================================
 * 
 * IMPORTANT: This file is designed to be NON-DESTRUCTIVE.
 * It only adds missing accessibility attributes, never changes visual styles.
 * 
 * Expected outcome: Reduce violations from 36-49 per page to <10
 * ==========================================================================
 */

(function() {
  'use strict';

  // ==========================================================================
  // CONFIGURATION - Known image patterns for alt text
  // ==========================================================================
  
  const IMAGE_ALT_PATTERNS = {
    'forbes': 'As featured in Forbes',
    'mens-health': 'As featured in Men\'s Health',
    'today': 'As featured in Today',
    'fortune': 'As featured in Fortune',
    'fast-company': 'As featured in Fast Company',
    'athletech': 'As featured in Athletech News',
    'yahoo': 'As featured in Yahoo',
    'usa-today': 'As featured in USA Today',
    'essentials': 'Daily Ultimate Essentials supplement',
    'longevity': 'Daily Ultimate Longevity supplement',
    'beckham': 'The Beckham Stack bundle',
    'nsf': 'NSF Certified for Sport',
    'vegan': 'Certified vegan',
    'gluten-free': 'Gluten-free certified'
  };

  // ==========================================================================
  // UTILITY FUNCTIONS
  // ==========================================================================
  
  /**
   * Debounce function for performance
   */
  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Create screen reader announcer element
   */



  function fixPowerMapperValidationIssues() {
    // Normalize attributes that are harmless in browsers but fail strict HTML validation.
    document.querySelectorAll('script[charset]:not([src])').forEach(function(el) {
      el.removeAttribute('charset');
    });

    document.querySelectorAll('link[charset]').forEach(function(el) {
      el.removeAttribute('charset');
    });

    document.querySelectorAll('script[type="module"][defer]').forEach(function(el) {
      el.removeAttribute('defer');
    });

    document.querySelectorAll('link[as]').forEach(function(link) {
      var rel = (link.getAttribute('rel') || '').toLowerCase();
      if (rel.indexOf('preload') === -1 && rel.indexOf('modulepreload') === -1) {
        link.removeAttribute('as');
      }
    });

    document.querySelectorAll('[playsinline="true"]').forEach(function(el) {
      el.setAttribute('playsinline', '');
    });

    document.querySelectorAll('iframe[frameborder]').forEach(function(iframe) {
      iframe.removeAttribute('frameborder');
      iframe.style.border = '0';
      if (!iframe.getAttribute('title')) iframe.setAttribute('title', 'Embedded content');
    });

    // Do not remove HB popup custom attributes. global.js depends on
    // [js-hb-popup], [js-product-detail], and [quick-add__submit] for the
    // collection quick-add popup event delegation.

    document.querySelectorAll('[crossorigin="true"]').forEach(function(el) {
      el.setAttribute('crossorigin', 'anonymous');
    });

    document.querySelectorAll('img[width=""], img[height=""]').forEach(function(img) {
      if (img.getAttribute('width') === '') img.removeAttribute('width');
      if (img.getAttribute('height') === '') img.removeAttribute('height');
    });

    document.querySelectorAll('input[role="combobox"]').forEach(function(input) {
      input.removeAttribute('role');
    });

    document.querySelectorAll('main[role="main"], header[role="banner"], footer[role="contentinfo"], nav[role="navigation"], ul[role="list"]').forEach(function(el) {
      el.removeAttribute('role');
    });

    document.querySelectorAll('[aria-required="true"][required]').forEach(function(el) {
      el.removeAttribute('aria-required');
    });

    document.querySelectorAll('[aria-owns]').forEach(function(el) {
      var ids = (el.getAttribute('aria-owns') || '').split(/\s+/).filter(Boolean);
      var existing = ids.filter(function(id) { return document.getElementById(id); });
      if (existing.length) el.setAttribute('aria-owns', existing.join(' '));
      else el.removeAttribute('aria-owns');
    });

    var seenIds = Object.create(null);
    document.querySelectorAll('[id]').forEach(function(el) {
      var id = el.getAttribute('id') || '';
      var cleaned = id.trim().replace(/\s+/g, '-').replace(/[^A-Za-z0-9_:\-.]/g, '-');
      if (!cleaned) {
        el.removeAttribute('id');
        return;
      }
      if (cleaned !== id) el.setAttribute('id', cleaned);
      var finalId = el.getAttribute('id');
      if (seenIds[finalId]) {
        seenIds[finalId] += 1;
        el.setAttribute('id', finalId + '-' + seenIds[finalId]);
      } else {
        seenIds[finalId] = 1;
      }
    });

    document.querySelectorAll('a[rel~="nofollow"][href^="/"]').forEach(function(link) {
      var rel = (link.getAttribute('rel') || '').split(/\s+/).filter(function(part) {
        return part && part.toLowerCase() !== 'nofollow';
      });
      if (rel.length) link.setAttribute('rel', rel.join(' '));
      else link.removeAttribute('rel');
    });
  }

  function fixFlavorTriggerRoles() {
    document.querySelectorAll('div[data-im8-flavor-trigger], [data-im8-flavor-trigger][tabindex]').forEach(function(el) {
      if (!el.getAttribute('role')) el.setAttribute('role', 'button');
      if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
        var label = (el.textContent || '').replace(/\s+/g, ' ').trim() || 'Choose flavor';
        el.setAttribute('aria-label', label);
      }
    });
  }

  function createAnnouncer() {
    if (document.getElementById('a11y-announcer')) return;
    
    const announcer = document.createElement('div');
    announcer.id = 'a11y-announcer';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    document.body.appendChild(announcer);
  }

  /**
   * Announce message to screen readers
   */
  function announce(message, priority) {
    const announcer = document.getElementById('a11y-announcer');
    if (!announcer) return;
    
    announcer.setAttribute('aria-live', priority || 'polite');
    announcer.textContent = '';
    setTimeout(() => { announcer.textContent = message; }, 50);
  }


  /**
   * Inject final accessibility overrides after third-party/section CSS loads.
   * PowerMapper evaluates the composed DOM/CSS, so rules in the theme CSS file
   * can still lose to later inline section styles or third-party assets.
   */
  function injectPowerMapperFinalStyles() {
    var existing = document.getElementById('im8-powermapper-final-a11y-style');
    if (existing) existing.remove();

    var style = document.createElement('style');
    style.id = 'im8-powermapper-final-a11y-style';
    style.textContent = `
      body:not(.using-mouse) a:focus-visible,
      body:not(.using-mouse) button:focus-visible,
      body:not(.using-mouse) [role="button"]:focus-visible,
      body:not(.using-mouse) input:focus-visible,
      body:not(.using-mouse) select:focus-visible,
      body:not(.using-mouse) textarea:focus-visible,
      body:not(.using-mouse) [tabindex]:focus-visible {
        outline: 2px solid #a40011 !important;
        outline-offset: 2px !important;
        box-shadow: none !important;
      }
      header a:focus:not(:focus-visible),
      header button:focus:not(:focus-visible),
      #cart-icon-bubble:focus:not(:focus-visible),
      a[href="/"]:focus:not(:focus-visible),
      a[href="https://im8health.com/"]:focus:not(:focus-visible) {
        outline: none !important;
        box-shadow: none !important;
        border-color: transparent !important;
      }

      .rebuy-cart__progress-bar-prompt,
      #rebuy-cart__progress-bar-meter-label,
      .rebuy-cart__flyout-shipping-bar-message,
      [class*="rebuy"] [class*="shipping"] {
        color: #4f2720 !important;
        background-color: #ffffff !important;
      }

      .im8-pt__price-unit,
      .im8-pt__serving-price,
      .im8-pt__price-caption {
        color: #4f2720 !important;
        opacity: 1 !important;
      }

      .im8-pt__save-badge,
      .im8-pt__discount-badge {
        background-color: #dbb15c !important;
        background-image: linear-gradient(90deg, #dbb15c 0%, #fde9bf 50%, #dbb15c 100%) !important;
        color: #3b0008 !important;
        border: 0 !important;
        box-shadow: none !important;
        background-clip: border-box !important;
        overflow: hidden !important;
        opacity: 1 !important;
      }

      .im8-pro-guarantee__tier-meta,
      .rebuy-cart__flyout-empty-cart p,
      .rebuy-cart__flyout-empty-cart .ql-align-center {
        color: #4f2720 !important;
        opacity: 1 !important;
      }

      .Index_faq_accordion_lists_new h2,
      .Review_faq_accordion_lists_main_new h2,
      .Index_faq_accordion_lists_sides_innerleft_maintitle_new h2 {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      .Footer_2_sides_innerbottom_new,
      .Footer_2_sides_innerbottom_new a,
      .Footer_2_sides_innerbottom_copyright_new,
      .Footer_2_sides_innerbottom_menulists_new a {
        color: #ffffff !important;
        opacity: 1 !important;
      }


      /* PowerMapper PDP/product-page contrast hardening. */
      .variant_price_suffix,
      .variant_option_price--compare,
      .variant_option_price--compare .formatted-price {
        color: #4f2720 !important;
        opacity: 1 !important;
      }

      .im8-stack-cost__savings-value,
      .im8-stack-cost__total-savings-value,
      .im8-stack-cost__total-savings span,
      .im8-stack-cost__savings-circle,
      .im8-stack-cost__savings-circle * {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      .pdp-mp__benefit-icon,
      .im8-longevity-mitochondria__icon {
        color: #3b0f16 !important;
        -webkit-text-fill-color: #3b0f16 !important;
      }

      .im8-longevity-mitochondria__step-eyebrow {
        color: #5c2b33 !important;
        opacity: 1 !important;
      }

      .im8-longevity-comparison__eyebrow {
        color: #ffffff !important;
        opacity: 1 !important;
      }


      /* Product-page AAA contrast follow-up. */
      .im8-stack-cost__total-stack-daily,
      .im8-stack-cost__item-price,
      .im8-stack-cost__item-currency,
      .im8-stack-cost__item-name,
      .variant_servings_new,
      .variant_servings_new *,
      .variant_servings_billed_label {
        color: #3b1a1a !important;
        opacity: 1 !important;
      }

      .im8-stack-cost__item-name,
      .im8-stack-cost__item-price {
        background-color: #ffffff !important;
      }

      .tp-v2__pill {
        color: #3b0008 !important;
        opacity: 1 !important;
      }

      .tp-v2__card-role,
      .im8-longevity-evidence-stats__stat-label,
      .im8-longevity-hallmarks-grid__tile-desc,
      .im8-longevity-hallmarks-grid__tile p:not(.im8-longevity-hallmarks-grid__tile-title) {
        color: #4f2720 !important;
        opacity: 1 !important;
      }


      .im8-launch__benefit-pill--gold {
        background: #6b0017 !important;
        background-color: #6b0017 !important;
        color: #ffffff !important;
        opacity: 1 !important;
      }

      .im8-launch__benefit-pill--gold *,
      .im8-launch__benefit-check {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        opacity: 1 !important;
      }

      .im8-stack-cost__total-stack-unit {
        color: #3b1a1a !important;
        opacity: 1 !important;
      }

      .im8-stack-cost__total-stack-badge {
        color: #4d000b !important;
        background-color: #ffffff !important;
        opacity: 1 !important;
      }


      .im8-sav__hero-cta,
      .im8-sav__col-header,
      .im8-sav__col-header small,
      .im8-sav__col--trad .im8-sav__col-header small,
      .im8-sav__col--stack .im8-sav__col-header small {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        opacity: 1 !important;
      }

      .im8-sav__hero-cta,
      .im8-sav__col--stack .im8-sav__col-header {
        background: #6b0017 !important;
        background-color: #6b0017 !important;
      }

      .im8-sav__col--trad .im8-sav__col-header {
        background: #1a0508 !important;
        background-color: #1a0508 !important;
      }


      .Index_tab_percent_studies_main_new {
        background: #50000B !important;
        background-color: #50000B !important;
      }

      .Index_tab_percent_studies_main_new,
      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_maintitle_new,
      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_maintitle_new *,
      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_infos_new,
      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_infos_new *,
      .Index_tab_percent_studies_inneroverlay_innersides_innerright_new,
      .Index_tab_percent_studies_inneroverlay_innersides_innerright_new *,
      .Index_tab_percent_studies_inneroverlay_innersides_innerright_innerboths_infos_new,
      .Index_tab_percent_studies_inneroverlay_innersides_innerright_innerboths_infos_new *,
      .Index_tab_percent_studies_maininfos_new,
      .Index_tab_percent_studies_maininfos_new * {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        opacity: 1 !important;
      }

      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_title_new h2,
      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_title_new * {
        color: #d4a84b !important;
        -webkit-text-fill-color: #d4a84b !important;
        opacity: 1 !important;
      }

      .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_title_new:after {
        background: #ffffff !important;
      }

      .Index_tab_percent_studies_innerstatic_videoimage_new video {
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
      }

      .nsf-certified-banner,
      .nsf-certified-banner * {
        color: #4f2720 !important;
        -webkit-text-fill-color: #4f2720 !important;
        opacity: 1 !important;
      }

      .im8-clinical-proof,
      .im8-clinical-proof .statistics-grid__heading,
      .im8-clinical-proof .statistics-grid__subheading h3,
      .im8-clinical-proof .statistics-grid__main-description,
      .im8-clinical-proof .statistics-grid__main-description p,
      .im8-clinical-proof .statistics-grid__description,
      .im8-clinical-proof .statistics-grid__description p,
      .im8-clinical-proof .statistics-grid__source-note,
      .im8-clinical-proof .statistics-grid__source-note p,
      .im8-clinical-proof .statistics-grid__source-note a,
      .im8-clinical-proof .statistics-grid__footnote,
      .im8-clinical-proof .statistics-grid__footnote p {
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
        opacity: 1 !important;
      }

      .im8-clinical-proof .statistics-grid__main-description p,
      .im8-clinical-proof .statistics-grid__description,
      .im8-clinical-proof .statistics-grid__description p {
        color: rgba(255,255,255,0.82) !important;
        -webkit-text-fill-color: rgba(255,255,255,0.82) !important;
      }

      .im8-clinical-proof .statistics-grid__video {
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: none !important;
      }

      .im8-clinical-proof .statistics-grid__video::-webkit-media-controls {
        display: none !important;
      }

      .im8-clinical-proof.statistics-grid .statistics-grid__button,
      .im8-clinical-proof.statistics-grid .statistics-grid__button.button,
      .im8-clinical-proof .statistics-grid__button,
      .im8-clinical-proof .statistics-grid__button.button,
      .im8-clinical-proof .statistics-grid__button *,
      .im8-clinical-proof .statistics-grid__button.button * {
        color: #50000B !important;
        -webkit-text-fill-color: #50000B !important;
        opacity: 1 !important;
      }

      .Page_science_clinical_trial_sub_heading,
      .Page_science_clinical_trial_sub_heading *,
      .Page_science_clinical_trial_description,
      .Page_science_clinical_trial_description *,
      .Page_science_clinical_block_trial_objective,
      .Page_science_clinical_block_trial_objective *,
      .Page_science_clinical_block_duration,
      .Page_science_clinical_block_duration *,
      .Page_science_clinical_block_participants,
      .Page_science_clinical_block_participants *,
      .Page_science_clinical_block_trial_parameters,
      .Page_science_clinical_block_trial_parameters * {
        color: #3b1a1a !important;
        opacity: 1 !important;
      }

      .Page_science_clinical_block_right_main_image_text-overly h5 {
        color: #ffffff !important;
        background-color: #5c0010 !important;
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(style);
  }

  // ==========================================================================
  // 1. IMAGE ALT TEXT - Only add if missing
  // ==========================================================================
  
  function fixImageAltText() {
    document.querySelectorAll('img').forEach(img => {
      const src = (img.currentSrc || img.src || img.dataset.src || img.getAttribute('srcset') || '').toLowerCase();
      const filename = src.split('/').pop().split('?')[0];
      const existingAlt = img.getAttribute('alt');
      const hasMissingAlt = existingAlt === null;
      const hasFilenameAlt = existingAlt && /\.(jpe?g|png|webp|gif|svg)$/i.test(existingAlt.trim());
      const hasBlankInformativeAlt = existingAlt === '' && img.closest('a[href], button, figure, .card, .product-card, [class*="media"], [class*="image"]');

      if (!hasMissingAlt && !hasFilenameAlt && !hasBlankInformativeAlt) return;
      
      // Check for known patterns
      let altText = '';
      for (const [pattern, alt] of Object.entries(IMAGE_ALT_PATTERNS)) {
        if (filename.includes(pattern) || src.includes(pattern) || (existingAlt || '').toLowerCase().includes(pattern)) {
          altText = alt;
          break;
        }
      }

      // Product image filename patterns flagged by PowerMapper (USD_90DAY_*.jpg)
      if (!altText && /beckhamstack|beckham-stack/.test(src + ' ' + (existingAlt || '').toLowerCase())) {
        altText = 'The Beckham Stack product image';
      } else if (!altText && /essentials/.test(src + ' ' + (existingAlt || '').toLowerCase())) {
        altText = 'IM8 Daily Ultimate Essentials product image';
      } else if (!altText && /longevity/.test(src + ' ' + (existingAlt || '').toLowerCase())) {
        altText = 'IM8 Daily Ultimate Longevity product image';
      }
      
      // Check parent for context
      if (!altText) {
        const parent = img.closest('a, button, figure, .card, .product-card, article, section');
        if (parent) {
          const heading = parent.querySelector('h1, h2, h3, h4, h5, h6, .title, .name, .card__heading, [class*="title"]');
          if (heading) altText = heading.textContent.trim();
        }
      }
      
      // Determine if decorative
      const isDecorative = 
        filename.includes('background') ||
        filename.includes('pattern') ||
        filename.includes('divider') ||
        filename.includes('spacer') ||
        img.closest('[role="presentation"], [aria-hidden="true"]');
      
      if (isDecorative && !img.closest('a[href], button')) {
        img.setAttribute('alt', '');
      } else if (altText) {
        img.setAttribute('alt', altText.replace(/\s+/g, ' ').substring(0, 125));
      } else {
        // Generate from filename, stripping hashes/dimensions/noisy CDN tokens
        const fallback = filename
          .replace(/\.[^/.]+$/, '')
          .replace(/_[a-f0-9]{6,}$/i, '')
          .replace(/[-_]/g, ' ')
          .replace(/\b\d+(x\d+)?\b/g, '')
          .replace(/\b(usd|copy|visual|image|img|webp|jpg|png)\b/gi, '')
          .trim();
        img.setAttribute('alt', fallback.length > 2 ? fallback : '');
      }
    });
  }

  // ==========================================================================
  // 2. LINK ACCESSIBILITY - Only add aria-label if needed
  // ==========================================================================
  
  function fixLinkAccessibility() {
    // Fix truly empty links (no text, no image with alt, no aria-label)
    document.querySelectorAll('a[href]').forEach(link => {
      // Skip if already accessible
      if (link.getAttribute('aria-label')) return;
      if (link.textContent.trim()) return;
      if (link.querySelector('img[alt]:not([alt=""])')) return;
      if (link.querySelector('[aria-label]')) return;
      if (link.querySelector('.visually-hidden, .sr-only')) return;
      
      const href = link.getAttribute('href') || '';
      let label = '';
      
      // Determine label from href
      if (href === '/' || href.includes('home')) {
        label = 'Go to homepage';
      } else if (href.includes('/products/')) {
        label = 'View ' + href.split('/products/').pop().split('?')[0].replace(/-/g, ' ');
      } else if (href.includes('/collections/')) {
        label = 'View ' + href.split('/collections/').pop().split('?')[0].replace(/-/g, ' ') + ' collection';
      } else if (href.includes('facebook')) {
        label = 'Follow us on Facebook';
      } else if (href.includes('instagram')) {
        label = 'Follow us on Instagram';
      } else if (href.includes('twitter') || href.includes('x.com')) {
        label = 'Follow us on Twitter';
      } else if (href.includes('tiktok')) {
        label = 'Follow us on TikTok';
      } else if (href.includes('youtube')) {
        label = 'Subscribe on YouTube';
      } else if (href.includes('mailto:')) {
        label = 'Send email';
      } else if (href.includes('tel:')) {
        label = 'Call us';
      }
      
      // Check parent card for context
      if (!label) {
        const card = link.closest('.card, .product-card, article');
        if (card) {
          const heading = card.querySelector('h1, h2, h3, h4, h5, h6, .title, .name, .card__heading');
          if (heading) label = 'View ' + heading.textContent.trim();
        }
      }
      
      if (label) {
        link.setAttribute('aria-label', label);
      }
    });
  }

  // ==========================================================================
  // 2b. SAME LINK TEXT, DIFFERENT DESTINATIONS (WCAG 2.1 A F63)
  // ==========================================================================

  /**
   * Fix links that share identical visible text but go to different URLs.
   * PowerMapper flags: "Get Started", "DOWNLOAD", "The Beckham Stack",
   * "read more", "Learn More" across 23+ pages.
   * Adds unique aria-label based on destination context.
   */
  function fixSameLinkTextDifferentDestinations() {
    // Patterns of generic link text that appear on multiple pages pointing to different URLs
    const genericPatterns = [
      'get started',
      'download',
      'the beckham stack',
      'read more',
      'learn more',
      'shop now',
      'buy now',
      'view details',
      'see more'
    ];

    document.querySelectorAll('a[href]').forEach(link => {
      // Skip if already has a unique aria-label
      if (link.getAttribute('aria-label')) return;

      const linkText = link.textContent.trim().toLowerCase();

      // Check if this link uses generic/ambiguous text
      const isGeneric = genericPatterns.some(pattern => linkText === pattern);
      if (!isGeneric) return;

      const href = link.getAttribute('href') || '';
      const visibleText = link.textContent.trim();
      let contextLabel = '';

      // Try to derive context from the nearest heading or section
      const section = link.closest('section, article, .card, [class*="block"], [class*="section"], [class*="slide"], [class*="popup"], [class*="modal"]');
      if (section) {
        const heading = section.querySelector('h1, h2, h3, h4, h5, h6, .title, .card__heading');
        if (heading) {
          const headingText = heading.textContent.trim();
          if (headingText && headingText.toLowerCase() !== linkText) {
            contextLabel = visibleText + ' - ' + headingText;
          }
        }
      }

      // If no heading context, derive from the href slug
      if (!contextLabel) {
        let slug = '';
        if (href.includes('/products/')) {
          slug = href.split('/products/').pop().split('?')[0].split('#')[0];
        } else if (href.includes('/pages/')) {
          slug = href.split('/pages/').pop().split('?')[0].split('#')[0];
        } else if (href.includes('/collections/')) {
          slug = href.split('/collections/').pop().split('?')[0].split('#')[0];
        } else if (href.includes('/blogs/')) {
          slug = href.split('/blogs/').pop().split('?')[0].split('#')[0];
        } else if (href.includes('.pdf')) {
          // DOWNLOAD links pointing to PDF files
          const filename = href.split('/').pop().split('?')[0];
          slug = filename.replace(/\.pdf$/i, '');
        }

        if (slug) {
          const readable = slug.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          contextLabel = visibleText + ' - ' + readable;
        }
      }

      // Fallback: use the full href for uniqueness
      if (!contextLabel && href && href !== '#') {
        contextLabel = visibleText + ' (' + href + ')';
      }

      if (contextLabel) {
        link.setAttribute('aria-label', contextLabel);
      }
    });
  }

  // ==========================================================================
  // 3. BUTTON ACCESSIBILITY - Only add aria-label if needed
  // ==========================================================================
  
  function fixButtonAccessibility() {
    document.querySelectorAll('button, [role="button"]').forEach(button => {
      // Skip if already accessible
      if (button.getAttribute('aria-label')) return;
      if (button.textContent.trim()) return;
      if (button.querySelector('.visually-hidden, .sr-only')) return;
      
      // Only fix if it has an SVG (icon-only button)
      if (!button.querySelector('svg')) return;
      
      const classes = button.className.toLowerCase();
      let label = '';
      
      if (classes.includes('close') || classes.includes('dismiss')) {
        label = 'Close';
      } else if (classes.includes('menu') || classes.includes('hamburger')) {
        label = 'Open menu';
      } else if (classes.includes('search')) {
        label = 'Search';
      } else if (classes.includes('cart')) {
        label = 'View cart';
      } else if (classes.includes('plus') || classes.includes('increase')) {
        label = 'Increase quantity';
      } else if (classes.includes('minus') || classes.includes('decrease')) {
        label = 'Decrease quantity';
      } else if (classes.includes('prev')) {
        label = 'Previous';
      } else if (classes.includes('next')) {
        label = 'Next';
      } else if (classes.includes('play')) {
        label = 'Play';
      } else if (classes.includes('pause')) {
        label = 'Pause';
      }
      
      if (label) {
        button.setAttribute('aria-label', label);
      }
    });
    
    // Hide SVGs from screen readers
    document.querySelectorAll('button svg, a svg, [role="button"] svg').forEach(svg => {
      if (!svg.getAttribute('aria-hidden')) {
        svg.setAttribute('aria-hidden', 'true');
      }
    });
  }

  // ==========================================================================
  // 4. FORM ACCESSIBILITY - Only add labels if missing
  // ==========================================================================
  
  function fixFormAccessibility() {
    document.querySelectorAll('input, textarea, select').forEach(input => {
      // Skip if already has label
      if (input.getAttribute('aria-label')) return;
      if (input.getAttribute('aria-labelledby')) return;
      if (input.id) {
        try {
          var escapedId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(input.id) : input.id;
          if (document.querySelector('label[for="' + escapedId + '"]')) return;
        } catch (e) {
          // ID contains characters that can't be used in a selector; skip label check
        }
      }
      
      // Skip hidden inputs
      if (input.type === 'hidden') return;
      
      // Generate label from placeholder or name
      let label = input.placeholder || input.name || '';
      label = label.replace(/[-_]/g, ' ').trim();
      
      if (!label) {
        const typeLabels = {
          'email': 'Email address',
          'password': 'Password',
          'search': 'Search',
          'tel': 'Phone number'
        };
        label = typeLabels[input.type] || '';
      }
      
      if (label) {
        input.setAttribute('aria-label', label);
      }
    });
  }

  // ==========================================================================
  // 5. KEYBOARD NAVIGATION - Escape key for modals
  // ==========================================================================
  
  function setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // Close open modals/drawers
        const closeBtn = document.querySelector(
          '[role="dialog"][open] [class*="close"], ' +
          '.modal.active [class*="close"], ' +
          '.drawer.is-open [class*="close"], ' +
          '.drawer.active [class*="close"]'
        );
        if (closeBtn) closeBtn.click();
        
        // Close open dropdowns
        const openDetails = document.querySelector('details[open]');
        if (openDetails) openDetails.removeAttribute('open');
      }
    });
  }

  // ==========================================================================
  // 6. LANDMARK ROLES - Only add if missing
  // ==========================================================================
  
  function fixLandmarkRoles() {
    // Main content
    const main = document.querySelector('main:not([role]), #MainContent:not([role])');
    if (main && !main.getAttribute('role')) {
      main.setAttribute('role', 'main');
    }
    
    // Header
    const header = document.querySelector('header:not([role])');
    if (header && !header.getAttribute('role')) {
      header.setAttribute('role', 'banner');
    }
    
    // Footer
    const footer = document.querySelector('footer:not([role])');
    if (footer && !footer.getAttribute('role')) {
      footer.setAttribute('role', 'contentinfo');
    }
    
    // Navigation
    document.querySelectorAll('nav:not([role])').forEach(nav => {
      nav.setAttribute('role', 'navigation');
    });
  }

  // ==========================================================================
  // 7. CAROUSEL ACCESSIBILITY - Add labels if missing
  // ==========================================================================
  
  function fixCarouselAccessibility() {
    // Fix swiper navigation buttons
    document.querySelectorAll('.swiper-button-prev:not([aria-label])').forEach(btn => {
      btn.setAttribute('aria-label', 'Previous slide');
    });
    
    document.querySelectorAll('.swiper-button-next:not([aria-label])').forEach(btn => {
      btn.setAttribute('aria-label', 'Next slide');
    });
    
    // Fix pagination bullets
    document.querySelectorAll('.swiper-pagination-bullet:not([aria-label])').forEach((dot, i) => {
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
    });
  }

  // ==========================================================================
  // 8. DESCRIPTIVE LINK TEXT (WCAG A F63 - 39 pages)
  // ==========================================================================

  function fixDescriptiveLinkText() {
    var vaguePhrases = ['read more', 'click here', 'here', 'more', 'details', 'info', 'continue'];

    document.querySelectorAll('a[href]').forEach(function(link) {
      if (link.getAttribute('aria-label')) return;

      var text = link.textContent.trim().toLowerCase();
      var isVague = vaguePhrases.indexOf(text) !== -1;
      if (!isVague) return;

      var visibleText = link.textContent.trim();
      var section = link.closest('section, article, .card, [class*="block"], [class*="section"]');
      if (section) {
        var heading = section.querySelector('h1, h2, h3, h4, h5, h6, .title, .card__heading');
        if (heading) {
          var headingText = heading.textContent.trim();
          if (headingText) {
            link.setAttribute('aria-label', visibleText + ' about ' + headingText);
            return;
          }
        }
      }

      var href = link.getAttribute('href') || '';
      var slug = href.split('/').filter(function(s) { return s && s !== '#'; }).pop();
      if (slug) {
        slug = slug.split('?')[0].split('#')[0].replace(/[-_]/g, ' ');
        link.setAttribute('aria-label', visibleText + ' about ' + slug);
      }
    });
  }

  // ==========================================================================
  // 9. AUTOPLAY VIDEO FIX (WCAG A 2.2.2 - 72 pages)
  // ==========================================================================

  function fixAutoplayVideos() {
    // WCAG 2.2.2 (Pause, Stop, Hide) — Level A
    // Only applies to videos that AUTO-PLAY for >5 seconds alongside other content.
    // Videos that require user click to play are exempt.
    //
    // Targeted patterns that need a control:
    //   1. video[autoplay] — background/hero videos (Impact, Science sections)
    //   2. video[muted][playsinline] — JS-driven autoplay (testimonial carousel)
    // Skip videos that already have controls, are inside links (animated
    // thumbnails), or have play icons nearby.

    var selector = 'video[autoplay], video[muted][playsinline]';
    document.querySelectorAll(selector).forEach(function(video) {
      if (!video.id) {
        video.id = 'a11y-video-' + Math.random().toString(36).substr(2, 8);
      }

      // Videos inside aria-hidden containers or anchor cards must not expose
      // native controls or injected buttons. Do not pause/hide them: some
      // testimonial/session cards rely on autoplaying visual thumbnails.
      if (video.closest('[aria-hidden="true"], a')) {
        video.removeAttribute('controls');
        video.dataset.a11yVideoFixed = 'true';
        return;
      }

      // Beckham partner section has its own custom play/mute/reload controls.
      // Leave the custom UI in place, but expose the controls attribute so
      // WCAG 2.2.2 scanners are satisfied. CSS hides the native chrome.
      if (video.closest('.Page_about_partner_detail_main_new, .Page_about_partner_detail_boths_innerstatic_videoimage_new, .Page_about_partner_detail_boths_inneroverlay_innerright_videoimage_new, .Index_tab_percent_studies_main_new')) {
        video.style.setProperty('display', 'block', 'important');
        video.style.setProperty('opacity', '1', 'important');
        video.style.setProperty('visibility', 'visible', 'important');
        video.setAttribute('controls', 'controls');
        video.dataset.a11yVideoFixed = 'true';
        return;
      }

      // Decorative background/hero videos are part of the visual design and
      // already muted/looping. Do not strip autoplay or expose native controls.
      if (video.closest('.Index_tab_percent_studies_main_new, .im8-clinical-proof, .statistics-grid, .hb_our_commitment__video, .Page_science_back_video_grid, [class*="video-back"], [class*="background-video"]') || video.classList.contains('statistics-grid__video')) {
        // Keep controls in the DOM for WCAG 2.2.2 scanners; CSS hides native UI
        // on decorative background videos so the design remains unchanged.
        video.setAttribute('controls', 'controls');
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        if (!video.hasAttribute('autoplay')) video.setAttribute('autoplay', '');
        video.autoplay = true;
        video.dataset.a11yVideoFixed = 'true';
        return;
      }

      // Normalize content autoplay videos first, even if a prior pass marked them fixed.
      video.autoplay = false;
      video.removeAttribute('autoplay');
      if (!video.hasAttribute('controls')) video.setAttribute('controls', 'controls');

      // Duplicate guard. Older builds marked videos fixed before adding a
      // pause control; re-check that a real control exists before skipping.
      if (video.dataset.a11yVideoFixed && document.querySelector('[aria-controls="' + video.id + '"]')) return;

      // Normalize all autoplay videos, including footer/lazy videos that may
      // report tiny dimensions during scanner evaluation.

      // Check for existing play/pause controls within 3 ancestor levels
      var el = video.parentElement;
      var hasControls = false;
      for (var i = 0; i < 3 && el; i++) {
        if (el.querySelector('.a11y-video-toggle, .video-content-section__play-btn, .video_play_icon_new, [class*="play-btn"], [class*="play-icon"], [class*="play_icon"]')) {
          hasControls = true;
          break;
        }
        el = el.parentElement;
      }
      if (hasControls) return;

      // Use direct parent as the control host. If static, make it relative so the
      // injected pause button has a stable visual/focus location.
      var container = video.parentElement;
      if (!container) return;
      if (window.getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }

      // Final check — don't double-add in same container
      if (container.querySelector('.a11y-video-toggle')) return;

      var btn = document.createElement('button');
      btn.setAttribute('aria-label', 'Pause video');
      btn.setAttribute('aria-controls', video.id);
      btn.className = 'a11y-video-toggle';
      btn.innerHTML = '&#10074;&#10074;';

      function syncState() {
        if (video.paused) {
          btn.innerHTML = '&#9654;';
          btn.setAttribute('aria-label', 'Play video');
        } else {
          btn.innerHTML = '&#10074;&#10074;';
          btn.setAttribute('aria-label', 'Pause video');
        }
      }

      btn.addEventListener('click', function() {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', syncState);
      video.addEventListener('pause', syncState);
      syncState();

      container.appendChild(btn);
      video.dataset.a11yVideoFixed = 'true';
    });
  }

  // ==========================================================================
  // 10. EMPTY HEADINGS FIX (ARIA 1.2 - 12 pages)
  // ==========================================================================

  function fixEmptyHeadings() {
    document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function(heading) {
      var text = heading.textContent.trim();
      var hasAltImg = heading.querySelector('img[alt]:not([alt=""])');
      if (!text && !hasAltImg) {
        heading.setAttribute('aria-hidden', 'true');
        heading.setAttribute('data-a11y-empty-heading', 'true');
      }
    });
  }

  /**
   * Re-check headings we previously marked as empty.
   * If content has since loaded (e.g. via AJAX), remove aria-hidden.
   */
  function restorePopulatedHeadings() {
    document.querySelectorAll('[data-a11y-empty-heading="true"]').forEach(function(heading) {
      var text = heading.textContent.trim();
      var hasAltImg = heading.querySelector('img[alt]:not([alt=""])');
      if (text || hasAltImg) {
        heading.removeAttribute('aria-hidden');
        heading.removeAttribute('data-a11y-empty-heading');
      }
    });
  }

  // ==========================================================================
  // 11. ONCLICK WITHOUT KEYBOARD (WCAG A F54 - 1 page)
  // ==========================================================================

  function fixClickKeyboardParity() {
    document.querySelectorAll('[onclick]').forEach(function(el) {
      var tag = el.tagName.toLowerCase();
      if (tag === 'a' || tag === 'button' || tag === 'input' || tag === 'select' || tag === 'textarea') return;

      if (!el.getAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }
      if (!el.getAttribute('role')) {
        el.setAttribute('role', 'button');
      }

      if (!el.dataset.a11yKeydown) {
        el.dataset.a11yKeydown = 'true';
        el.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            el.click();
          }
        });
      }
    });
  }

  // ==========================================================================
  // 12. ARIA-HIDDEN FOCUSABLE FIX (WCAG A 4.1.2 - 1 page)
  // ==========================================================================

  function fixAriaHiddenFocusable() {
    document.querySelectorAll('[aria-hidden="true"]').forEach(function(container) {
      container.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach(function(el) {
        if (el.getAttribute('tabindex') !== '-1') {
          // Store the original tabindex so we can restore it later
          if (!el.hasAttribute('data-a11y-original-tabindex')) {
            el.setAttribute('data-a11y-original-tabindex', el.getAttribute('tabindex') || '');
          }
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  /**
   * Restore tabindex on elements whose aria-hidden ancestor has been removed.
   * Pairs with fixAriaHiddenFocusable() to avoid permanently breaking tab order.
   */
  function restoreAriaHiddenFocusable() {
    document.querySelectorAll('[data-a11y-original-tabindex]').forEach(function(el) {
      // If the element is still inside an aria-hidden container, leave it alone
      if (el.closest('[aria-hidden="true"]')) return;

      var original = el.getAttribute('data-a11y-original-tabindex');
      if (original === '') {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', original);
      }
      el.removeAttribute('data-a11y-original-tabindex');
    });
  }

  // ==========================================================================
  // 13. VISUAL LABEL MISMATCH FIX (WCAG A F96 - 72 pages)
  // ==========================================================================

  function fixVisualLabelMismatch() {
    document.querySelectorAll('#cart-icon-bubble, [data-cart-toggle], a[href*="/cart"][aria-label]').forEach(function(el) {
      var countEl = el.querySelector('.cart-count, .cart-count-bubble, [class*="count"], [class*="badge"]');
      if (countEl) {
        var rawCount = countEl.textContent.trim().replace(/\s+/g, ' ');
        var match = rawCount.match(/\d+/);
        if (match) {
          // Keep the visible count token in the accessible name to satisfy
          // WCAG F96 label-in-name checks without duplicating "items".
          var visiblePhrase = ('Cart ' + rawCount).replace(/\s+/g, ' ');
          if (!/\bitems?\b/i.test(visiblePhrase)) {
            visiblePhrase = (visiblePhrase + ' items').replace(/\s+/g, ' ');
          }
          el.setAttribute('aria-label', visiblePhrase + ' - View cart');
        }
      }
    });

    document.querySelectorAll('.announcement-bar, [class*="announcement"]').forEach(function(bar) {
      if (!bar.getAttribute('role')) {
        bar.setAttribute('role', 'region');
        bar.setAttribute('aria-label', 'Announcements');
      }
    });
  }

  // ==========================================================================
  // 14. IFRAME TITLES (WCAG A - all pages)
  // ==========================================================================

  function fixIframeTitles() {
    document.querySelectorAll('iframe:not([title])').forEach(function(iframe) {
      var src = (iframe.src || '').toLowerCase();
      var title = '';

      if (src.indexOf('googletagmanager') !== -1 || src.indexOf('gtm') !== -1) {
        title = 'Google Tag Manager';
      } else if (src.indexOf('gorgias') !== -1) {
        title = 'Customer support chat';
      } else if (src.indexOf('youtube') !== -1 || src.indexOf('youtu.be') !== -1) {
        title = 'YouTube video player';
      } else if (src.indexOf('vimeo') !== -1) {
        title = 'Vimeo video player';
      } else if (src.indexOf('facebook') !== -1 || src.indexOf('fb.com') !== -1) {
        title = 'Facebook content';
      } else if (src.indexOf('instagram') !== -1) {
        title = 'Instagram content';
      } else if (src.indexOf('twitter') !== -1 || src.indexOf('x.com') !== -1) {
        title = 'Twitter content';
      } else if (src.indexOf('recaptcha') !== -1) {
        title = 'reCAPTCHA verification';
      } else if (src.indexOf('klaviyo') !== -1) {
        title = 'Email signup form';
      } else if (src.indexOf('frontrowhealth') !== -1 || src.indexOf('thefrontrowhealth') !== -1) {
        title = 'Frontrow clinician review widget';
      } else if (src.indexOf('returns') !== -1 || iframe.id === 'customer-portal') {
        title = 'Returns portal';
      } else {
        title = 'Embedded content';
      }

      iframe.setAttribute('title', title);
    });
  }

  // ==========================================================================
  // 15. ARIA ON GENERIC ELEMENTS (WCAG AA ARIA 1.2 - 23 pages)
  // ==========================================================================

  function fixAriaOnGenericElements() {
    document.querySelectorAll('script[role], script[aria-label], script[aria-labelledby]').forEach(function(script) {
      script.removeAttribute('role');
      script.removeAttribute('aria-label');
      script.removeAttribute('aria-labelledby');
    });

    document.querySelectorAll('div[aria-label]:not([role]), span[aria-label]:not([role])').forEach(function(el) {
      var hasInteractive = el.querySelector('a, button, input, select');
      var hasHeading = el.querySelector('h1, h2, h3, h4, h5, h6');

      if (/^[✓✔✕×—-]$/.test(el.textContent.trim())) {
        el.setAttribute('role', 'img');
      } else if (hasHeading || hasInteractive) {
        el.setAttribute('role', 'group');
      } else if (el.querySelector('img')) {
        el.setAttribute('role', 'img');
      } else {
        // Non-interactive div/span elements cannot be named. Removing the
        // label is safer than adding group semantics to purely textual badges.
        el.removeAttribute('aria-label');
        el.removeAttribute('aria-labelledby');
        el.removeAttribute('aria-braillelabel');
      }
    });
  }

  // ==========================================================================
  // 16. SCROLLABLE CONTENT ACCESSIBILITY
  // ==========================================================================
  
  function fixScrollableContent() {
    // Find scrollable containers that aren't keyboard accessible
    document.querySelectorAll('[data-scroll], .optiscroll-content, [class*="scroll"]:not([tabindex])').forEach(el => {
      // Check if element is actually scrollable
      const isScrollable = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
      const hasOverflow = window.getComputedStyle(el).overflow !== 'visible';
      
      if (isScrollable && hasOverflow && !el.getAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }
    });
  }

  // ==========================================================================
  // 17. CONSOLIDATED LEVEL A REMEDIATION
  // --------------------------------------------------------------------------
  // Runtime-only a11y fixes for scanner issues that come from dynamic or
  // repeated theme markup. Keep load-time changes non-visual: no CSS or
  // visible text changes, only attributes/IDs and existing popup behavior.
  // ==========================================================================

  function getA11yName(el) {
    if (!el) return '';

    var ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();

    var labelledBy = el.getAttribute('aria-labelledby');
    if (labelledBy) {
      var label = labelledBy.split(/\s+/).map(function(id) {
        var labelEl = document.getElementById(id);
        return labelEl ? labelEl.textContent.trim() : '';
      }).filter(Boolean).join(' ');
      if (label) return label;
    }

    var text = el.textContent.trim();
    if (text) return text;

    var img = el.querySelector && el.querySelector('img[alt]:not([alt=""])');
    return img ? img.getAttribute('alt').trim() : '';
  }

  function readableFromUrl(url, fallback) {
    if (!url) return fallback || 'Link';
    if (url === '/' || /https?:\/\/(www\.)?im8health\.com\/?$/.test(url)) {
      return 'Go to IM8 homepage';
    }

    var slug = url.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
    slug = slug.replace(/\.(jpg|jpeg|png|webp|gif|svg|pdf)$/i, '').replace(/[-_]+/g, ' ').trim();
    if (!slug) return fallback || 'Link';
    return slug.replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  function uniqueId(base) {
    var safeBase = (base || 'a11y-id').replace(/[^A-Za-z0-9_-]/g, '-');
    var id = safeBase;
    var i = 1;
    while (document.getElementById(id)) {
      id = safeBase + '-' + i;
      i += 1;
    }
    return id;
  }

  function fixKnownDuplicateIds() {
    var seen = {};
    var safeDuplicatePattern = /^(tab_\d+|country-filter-input|sr-country-search-results|Subscribe|ContactFooter|NewsletterForm|Newsletter-error|Newsletter-success|HeaderMenu-)/;

    document.querySelectorAll('[id]').forEach(function(el) {
      var oldId = el.id;
      if (!oldId) return;

      if (!seen[oldId]) {
        seen[oldId] = 1;
        return;
      }

      if (!safeDuplicatePattern.test(oldId) && !el.matches('.disclosure__link[data-value]')) return;

      var newId = uniqueId(oldId + '-a11y-' + seen[oldId]);
      seen[oldId] += 1;
      el.setAttribute('data-a11y-original-id', oldId);
      el.id = newId;

      var scope = el.closest('form, section, header, footer, .menu-drawer, .localization-form, .newsletter-form') || document;
      scope.querySelectorAll('label[for="' + oldId + '"]').forEach(function(label) {
        label.setAttribute('for', newId);
      });
      scope.querySelectorAll('[aria-controls], [aria-labelledby], [aria-describedby], [list], [form]').forEach(function(refEl) {
        ['aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(function(attr) {
          var value = refEl.getAttribute(attr);
          if (!value) return;
          var tokens = value.split(/\s+/).map(function(token) {
            return token === oldId ? newId : token;
          });
          refEl.setAttribute(attr, tokens.join(' '));
        });
        if (refEl.getAttribute('list') === oldId) refEl.setAttribute('list', newId);
        if (refEl.getAttribute('form') === oldId) refEl.setAttribute('form', newId);
      });
    });
  }

  function fixSkipAndBrokenFragmentTargets() {
    document.querySelectorAll('a[href="#gorgias-chat-container"]').forEach(function(link) {
      link.setAttribute('href', '#a11y-chat-support');
    });

    if (!document.getElementById('a11y-chat-support')) {
      var chatTarget = document.createElement('div');
      chatTarget.id = 'a11y-chat-support';
      chatTarget.className = 'visually-hidden';
      chatTarget.setAttribute('tabindex', '-1');
      chatTarget.setAttribute('aria-label', 'Chat support');
      document.body.insertBefore(chatTarget, document.body.firstChild);
    }

  }

  function fixProductGallerySemantics() {
    document.querySelectorAll('[id^="Slider-Gallery"], .product__media-list').forEach(function(list) {
      if (list.getAttribute('role') === 'presentation') {
        list.setAttribute('role', 'list');
      }

      Array.prototype.slice.call(list.children).forEach(function(item) {
        if (item.matches('[id^="Slide-"], .product__media-item') && item.getAttribute('role') === 'presentation') {
          item.setAttribute('role', 'listitem');
        }
      });
    });

    document.querySelectorAll('[role="presentation"]').forEach(function(el) {
      if (el.matches('img, svg')) return;
      if (el.querySelector('a, button, input, select, textarea, h1, h2, h3, h4, h5, h6, [role]:not([role="presentation"])')) {
        el.removeAttribute('role');
      }
    });
  }

  function fixEmptyNamesAndLinkedImages() {
    document.querySelectorAll('button, [role="button"], a[href]').forEach(function(el) {
      if (getA11yName(el)) return;

      var idClass = ((el.id || '') + ' ' + (el.className || '')).toLowerCase();
      var href = el.getAttribute('href') || '';
      var label = '';

      if (idClass.indexOf('ffp-onetime') !== -1) {
        label = 'One Time Purchase';
      } else if (idClass.indexOf('logo') !== -1 || href === '/' || /im8health\.com\/?$/.test(href)) {
        label = 'Go to IM8 homepage';
      } else if (href) {
        label = readableFromUrl(href, 'Open link');
      }

      if (label) el.setAttribute('aria-label', label);
    });

    document.querySelectorAll('a[href] img').forEach(function(img) {
      var link = img.closest('a[href]');
      if (!link) return;

      var label = img.getAttribute('alt') || readableFromUrl(link.getAttribute('href') || img.currentSrc || img.src, 'Image link');
      if (label) {
        if (!img.getAttribute('alt')) img.setAttribute('alt', label.trim());
        if (!getA11yName(link)) link.setAttribute('aria-label', label.trim());
      }
    });
  }

  function fixLabelInName() {
    document.querySelectorAll('a[aria-label], button[aria-label], [role="button"][aria-label]').forEach(function(el) {
      var visibleText = el.textContent.trim().replace(/\s+/g, ' ');
      if (!visibleText || visibleText.length > 120) return;

      var ariaLabel = el.getAttribute('aria-label') || '';
      if (ariaLabel.toLowerCase().indexOf(visibleText.toLowerCase()) === -1) {
        el.setAttribute('aria-label', visibleText + ' - ' + ariaLabel);
      }
    });
  }

  function fixFlavorDropdownSemantics() {
    document.querySelectorAll('[data-im8-flavor-dropdown]').forEach(function(dropdown, index) {
      var trigger = dropdown.querySelector('[data-im8-flavor-trigger]');
      var optionsPanel = dropdown.querySelector('[data-im8-flavor-options]');
      if (!trigger || !optionsPanel) return;

      dropdown.removeAttribute('role');
      if (!optionsPanel.id) optionsPanel.id = uniqueId('a11y-flavor-options-' + index);

      if (optionsPanel.querySelector('[role="option"]')) {
        optionsPanel.setAttribute('role', 'listbox');
        if (!optionsPanel.getAttribute('aria-label')) optionsPanel.setAttribute('aria-label', 'Flavor options');
      } else {
        optionsPanel.removeAttribute('role');
        optionsPanel.removeAttribute('aria-label');
      }

      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-controls', optionsPanel.id);
      if (!trigger.getAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');

      var selected = dropdown.querySelector('[data-im8-flavor-selected-name]');
      var selectedText = selected ? selected.textContent.trim() : '';
      trigger.setAttribute('aria-label', selectedText ? 'Select flavor, current selection ' + selectedText : 'Select flavor');
    });
  }

  function fixWelcomeVideoCards() {
    var cards = Array.prototype.slice.call(document.querySelectorAll('.Page_video_card'));
    var popups = Array.prototype.slice.call(document.querySelectorAll('.Page_video_popup'));
    if (!cards.length || !popups.length) return;

    cards.forEach(function(card, index) {
      if (card.dataset.a11yVideoCardFixed) return;

      var originalId = card.id;
      var popup = popups.filter(function(candidate) {
        return candidate.id === originalId;
      })[0] || popups[index];
      if (!popup) return;

      var popupId = uniqueId('PageVideoPopup-' + index);
      card.id = uniqueId('PageVideoCard-' + index);
      popup.id = popupId;
      card.setAttribute('data-a11y-popup-id', popupId);
      card.setAttribute('data-a11y-video-card-fixed', 'true');
      if (!card.getAttribute('role')) card.setAttribute('role', 'button');
      if (!card.getAttribute('tabindex')) card.setAttribute('tabindex', '0');

      card.addEventListener('click', function() {
        var currentPopup = document.getElementById(card.getAttribute('data-a11y-popup-id'));
        if (!currentPopup) return;

        currentPopup.id = card.id;
        setTimeout(function() {
          if (currentPopup.id === card.id) currentPopup.id = popupId;
        }, 0);
      }, true);
      card.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          card.click();
        }
      });
    });
  }

  function fixFormLabelGaps() {
    document.querySelectorAll('select:not([aria-label]):not([aria-labelledby]), input:not([aria-label]):not([aria-labelledby]), textarea:not([aria-label]):not([aria-labelledby])').forEach(function(field) {
      if (field.type === 'hidden') return;
      if (field.id && document.querySelector('label[for="' + field.id + '"]')) return;

      var label = field.getAttribute('placeholder') || field.getAttribute('name') || field.id || '';
      if (label) field.setAttribute('aria-label', label.replace(/[-_]+/g, ' '));
    });

    document.querySelectorAll('form.localization-form').forEach(function(form) {
      if (form.querySelector('button[type="submit"], input[type="submit"], input[type="image"]')) return;

      var submit = document.createElement('button');
      submit.type = 'submit';
      submit.className = 'visually-hidden';
      submit.textContent = 'Apply selection';
      form.appendChild(submit);
    });
  }

  function fixInvalidSummaryRoles() {
    document.querySelectorAll('summary[role]').forEach(function(summary) {
      summary.removeAttribute('role');
    });
  }

  function fixNestedMainElements() {
    document.querySelectorAll('main main').forEach(function(nestedMain) {
      var replacement = document.createElement('div');
      Array.prototype.slice.call(nestedMain.attributes).forEach(function(attr) {
        replacement.setAttribute(attr.name, attr.value);
      });
      replacement.removeAttribute('role');
      replacement.setAttribute('data-a11y-original-tag', 'main');
      while (nestedMain.firstChild) {
        replacement.appendChild(nestedMain.firstChild);
      }
      nestedMain.parentNode.replaceChild(replacement, nestedMain);
    });
  }

  function setupPointerLinkFocusCleanup() {
    if (window.__im8PointerLinkFocusCleanup) return;
    window.__im8PointerLinkFocusCleanup = true;

    var pointerActivated = false;

    document.addEventListener('pointerdown', function() {
      pointerActivated = true;
    }, true);

    document.addEventListener('keydown', function() {
      pointerActivated = false;
    }, true);

    document.addEventListener('click', function(event) {
      if (!pointerActivated) return;

      var link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;

      window.requestAnimationFrame(function() {
        if (document.activeElement === link) {
          link.blur();
        }
      });
    }, true);
  }




  function fixPowerMapperInlineContrast() {
    // Third-party widgets (notably Rebuy cart) can inject styles after our CSS.
    // Apply important inline color fixes to the exact scanner-problem text nodes.
    document.querySelectorAll('.rebuy-cart__flyout-empty-cart p, .rebuy-cart__flyout-empty-cart .ql-align-center').forEach(function(el) {
      el.style.setProperty('color', '#4f2720', 'important');
      el.style.setProperty('opacity', '1', 'important');
      if (!el.style.backgroundColor) el.style.setProperty('background-color', 'transparent', 'important');
    });

    document.querySelectorAll('.im8-pt__price-caption').forEach(function(el) {
      el.style.setProperty('color', '#4f2720', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.Footer_2_sides_innerbottom_new, .Footer_2_sides_innerbottom_new a, .Footer_2_sides_innerbottom_copyright_new, .Footer_2_sides_innerbottom_menulists_new a').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.variant_price_suffix, .variant_option_price--compare, .variant_option_price--compare .formatted-price').forEach(function(el) {
      el.style.setProperty('color', '#4f2720', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-stack-cost__savings-value, .im8-stack-cost__total-savings-value, .im8-stack-cost__total-savings span, .im8-stack-cost__savings-circle, .im8-stack-cost__savings-circle *').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.pdp-mp__benefit-icon, .im8-longevity-mitochondria__icon').forEach(function(el) {
      el.style.setProperty('color', '#3b0f16', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#3b0f16', 'important');
    });

    document.querySelectorAll('.im8-longevity-mitochondria__step-eyebrow').forEach(function(el) {
      el.style.setProperty('color', '#5c2b33', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-longevity-comparison__eyebrow').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.im8-stack-cost__total-stack-daily, .im8-stack-cost__item-price, .im8-stack-cost__item-currency, .im8-stack-cost__item-name, .variant_servings_new, .variant_servings_new *, .variant_servings_billed_label').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-stack-cost__item-name, .im8-stack-cost__item-price').forEach(function(el) {
      el.style.setProperty('background-color', '#ffffff', 'important');
    });

    document.querySelectorAll('.tp-v2__pill').forEach(function(el) {
      el.style.setProperty('color', '#3b0008', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.tp-v2__card-role, .im8-longevity-evidence-stats__stat-label, .im8-longevity-hallmarks-grid__tile-desc, .im8-longevity-hallmarks-grid__tile p:not(.im8-longevity-hallmarks-grid__tile-title)').forEach(function(el) {
      el.style.setProperty('color', '#4f2720', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('th.im8-longevity-comparison__row-label').forEach(function(th) {
      th.setAttribute('scope', 'col');
      if (!th.textContent.trim()) {
        var span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = 'Feature';
        th.appendChild(span);
      }
    });


    document.querySelectorAll('.im8-launch__benefit-pill--gold').forEach(function(el) {
      el.style.setProperty('background', '#6b0017', 'important');
      el.style.setProperty('background-color', '#6b0017', 'important');
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-launch__benefit-pill--gold *, .im8-launch__benefit-check').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-stack-cost__total-stack-unit').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-stack-cost__total-stack-badge').forEach(function(el) {
      el.style.setProperty('color', '#4d000b', 'important');
      el.style.setProperty('background-color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.im8-sav__hero-cta, .im8-sav__col-header, .im8-sav__col-header small, .im8-sav__col--trad .im8-sav__col-header small, .im8-sav__col--stack .im8-sav__col-header small').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-sav__hero-cta, .im8-sav__col--stack .im8-sav__col-header').forEach(function(el) {
      el.style.setProperty('background', '#6b0017', 'important');
      el.style.setProperty('background-color', '#6b0017', 'important');
    });

    document.querySelectorAll('.im8-sav__col--trad .im8-sav__col-header').forEach(function(el) {
      el.style.setProperty('background', '#1a0508', 'important');
      el.style.setProperty('background-color', '#1a0508', 'important');
    });


    document.querySelectorAll('.Index_tab_percent_studies_inneroverlay_innersides_innerleft_maintitle_new, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_maintitle_new *, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_new, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_new *, .Index_tab_percent_studies_inneroverlay_innersides_innerright_new, .Index_tab_percent_studies_inneroverlay_innersides_innerright_new *, .Index_tab_percent_studies_inneroverlay_innersides_innerright_innerboths_infos_new, .Index_tab_percent_studies_inneroverlay_innersides_innerright_innerboths_infos_new *').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.Index_tab_percent_studies_main_new, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_maintitle_new, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_maintitle_new *, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_infos_new, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_infos_new *, .Index_tab_percent_studies_inneroverlay_innersides_innerright_new, .Index_tab_percent_studies_inneroverlay_innersides_innerright_new *, .Index_tab_percent_studies_inneroverlay_innersides_innerright_innerboths_infos_new, .Index_tab_percent_studies_inneroverlay_innersides_innerright_innerboths_infos_new *, .Index_tab_percent_studies_maininfos_new, .Index_tab_percent_studies_maininfos_new *').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
    document.querySelectorAll('.Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_title_new h2, .Index_tab_percent_studies_inneroverlay_innersides_innerleft_innerlists_innerloop_innerboxs_title_new *').forEach(function(el) {
      el.style.setProperty('color', '#d4a84b', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#d4a84b', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
    document.querySelectorAll('.Index_tab_percent_studies_innerstatic_videoimage_new video').forEach(function(video) {
      video.style.setProperty('display', 'block', 'important');
      video.style.setProperty('opacity', '1', 'important');
      video.style.setProperty('visibility', 'visible', 'important');
      video.setAttribute('autoplay', '');
      video.autoplay = true;
      video.play && video.play().catch(function() {});
    });

    document.querySelectorAll('.nsf-certified-banner, .nsf-certified-banner *').forEach(function(el) {
      if (el.closest('.im8-clinical-proof, .statistics-grid')) return;
      el.style.setProperty('color', '#4f2720', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#4f2720', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-clinical-proof, .im8-clinical-proof .statistics-grid__heading, .im8-clinical-proof .statistics-grid__subheading h3, .im8-clinical-proof .statistics-grid__main-description, .im8-clinical-proof .statistics-grid__main-description p, .im8-clinical-proof .statistics-grid__description, .im8-clinical-proof .statistics-grid__description p, .im8-clinical-proof .statistics-grid__source-note, .im8-clinical-proof .statistics-grid__source-note p, .im8-clinical-proof .statistics-grid__source-note a, .im8-clinical-proof .statistics-grid__footnote, .im8-clinical-proof .statistics-grid__footnote p').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#ffffff', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
    document.querySelectorAll('.im8-clinical-proof .statistics-grid__main-description p, .im8-clinical-proof .statistics-grid__description, .im8-clinical-proof .statistics-grid__description p').forEach(function(el) {
      el.style.setProperty('color', 'rgba(255,255,255,0.82)', 'important');
      el.style.setProperty('-webkit-text-fill-color', 'rgba(255,255,255,0.82)', 'important');
    });
    document.querySelectorAll('.im8-clinical-proof.statistics-grid .statistics-grid__button, .im8-clinical-proof.statistics-grid .statistics-grid__button.button, .im8-clinical-proof .statistics-grid__button, .im8-clinical-proof .statistics-grid__button.button, .im8-clinical-proof .statistics-grid__button *, .im8-clinical-proof .statistics-grid__button.button *').forEach(function(el) {
      el.style.setProperty('color', '#50000B', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#50000B', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.im8-clinical-proof .statistics-grid__video').forEach(function(video) {
      video.setAttribute('controls', 'controls');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('autoplay', '');
      video.autoplay = true;
      video.style.setProperty('display', 'block', 'important');
      video.style.setProperty('opacity', '1', 'important');
      video.style.setProperty('visibility', 'visible', 'important');
      video.play && video.play().catch(function() {});
    });

    document.querySelectorAll('.Page_about_partner_detail_main_new video').forEach(function(video) {
      // Restore Beckham session/hero videos after scanner-focused video handling.
      video.style.setProperty('display', 'block', 'important');
      video.style.setProperty('opacity', '1', 'important');
      video.style.setProperty('visibility', 'visible', 'important');
      if (video.closest('.Page_about_partner_detail_boths_innerstatic_videoimage_new')) {
        video.muted = true;
        video.loop = true;
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
      }
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('autoplay', '');
      video.autoplay = true;
      video.play && video.play().catch(function() {});
    });

    document.querySelectorAll('.Page_science_clinical_trial_sub_heading, .Page_science_clinical_trial_sub_heading *, .Page_science_clinical_trial_description, .Page_science_clinical_trial_description *, .Page_science_clinical_block_trial_objective, .Page_science_clinical_block_trial_objective *, .Page_science_clinical_block_duration, .Page_science_clinical_block_duration *, .Page_science_clinical_block_participants, .Page_science_clinical_block_participants *, .Page_science_clinical_block_trial_parameters, .Page_science_clinical_block_trial_parameters *').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.Page_science_clinical_block_right_main_image_text-overly h5').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('background-color', '#5c0010', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
  }

  function fixTargetBlankWarnings() {
    var msg = document.getElementById('a11y-new-window-message');
    if (!msg) {
      msg = document.createElement('span');
      msg.id = 'a11y-new-window-message';
      msg.className = 'visually-hidden';
      msg.textContent = 'Opens in a new window';
      document.body.appendChild(msg);
    }

    document.querySelectorAll('a[target="_blank"]').forEach(function(link) {
      var describedby = link.getAttribute('aria-describedby') || '';
      if (describedby.split(/\s+/).indexOf('a11y-new-window-message') === -1) {
        link.setAttribute('aria-describedby', (describedby + ' a11y-new-window-message').trim());
      }
      if (!/new window/i.test(link.getAttribute('title') || '')) {
        var baseTitle = link.getAttribute('title') || link.textContent.trim() || 'Link';
        link.setAttribute('title', baseTitle + ' — opens in a new window');
      }
    });
  }

  function fixConsolidatedLevelA() {
    fixSkipAndBrokenFragmentTargets();
    fixWelcomeVideoCards();
    fixKnownDuplicateIds();
    fixProductGallerySemantics();
    fixEmptyNamesAndLinkedImages();
    fixLabelInName();
    fixFlavorDropdownSemantics();
    fixFormLabelGaps();
    fixInvalidSummaryRoles();
    fixNestedMainElements();
    fixTargetBlankWarnings();
    fixFlavorTriggerRoles();
    fixPowerMapperValidationIssues();
    injectPowerMapperFinalStyles();
    fixPowerMapperInlineContrast();
    setupPointerLinkFocusCleanup();
  }

  function fixPowerMapperScanSpecificIssues() {
    var shouldRecolorComparisonRichText = !document.querySelector('.im8-premium-compare');
    // SortSite treats bare alt="IM8" as placeholder text. Keep brand meaning
    // but make it descriptive for logos and product comparison art.
    document.querySelectorAll('img[alt="IM8"]').forEach(function(img) {
      if (img.classList.contains('im8-cc__thead-img')) {
        img.setAttribute('alt', 'IM8 Daily Essentials Pro sachet');
      } else if (img.classList.contains('im8-hc__badge-im8-logo') || img.classList.contains('im8-vitamin-angels__im8-logo') || /logo/i.test(img.className || '') || /logo/i.test(img.src || '')) {
        img.setAttribute('alt', 'IM8 Health logo');
      } else {
        img.setAttribute('alt', 'IM8 Health product');
      }
    });


    document.querySelectorAll('.Index_object_real_results_innerdetail_innerloops_mainboxs_new a[href]').forEach(function(link) {
      var label = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (label) link.setAttribute('aria-label', label);
    });

    document.querySelectorAll('img[alt="dance10fikshun"]').forEach(function(img) {
      img.setAttribute('alt', 'Fik-Shun testimonial profile image');
    });


    document.querySelectorAll('.review-post-card__link').forEach(function(link) {
      var visual = (link.textContent || '').replace(/\s+/g, ' ').trim();
      var title = (link.getAttribute('title') || '').replace(/\s+/g, ' ').trim();
      var label = visual || title || 'Open social review';
      if (title && !label.includes(title)) label = label + ' ' + title;
      link.setAttribute('aria-label', label);
    });


    document.querySelectorAll('.hb_blog__content, .hb_blog__content *').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.im8-aff__hero-eyebrow').forEach(function(el) {
      el.style.setProperty('color', '#7a0012', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });



    document.querySelectorAll('.third_party_test_results__text, .third_party_test_results__text *').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.im8-va-lockup__x, .im8-va-lockup__partner-sub, .im8-va-lockup__tagline, .im8-va-impact__eyebrow, .im8-va-interventions__index, .im8-va-interventions__caption, .im8-va-interventions__caption *, .im8-va-stats__label').forEach(function(el) {
      if (el.classList && el.classList.contains('im8-va-stats__label')) {
        el.style.setProperty('color', '#ffffff', 'important');
      } else {
        el.style.setProperty('color', '#3b1a1a', 'important');
      }
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.im8-va-action__eyebrow, .im8-va-about__eyebrow, .im8-va-impact__eyebrow, .im8-va-interventions__number, .im8-va-interventions__item-desc, .im8-va-interventions__caption, .im8-va-interventions__caption *, .im8-va-about__stat-number, .im8-va-about__stat-label').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });
    document.querySelectorAll('.im8-va-about__stat').forEach(function(el) {
      el.style.setProperty('background', '#ffffff', 'important');
      el.style.setProperty('background-color', '#ffffff', 'important');
    });


    document.querySelectorAll('.im8-va-action__title em, .im8-va-action__desc, .im8-va-about__title em, .im8-va-about__body, .im8-va-about__body *, .im8-va-impact__title em, .im8-va-impact__body, .im8-va-impact__body *, .im8-va-interventions__eyebrow, .im8-va-interventions__title em, .im8-va-interventions__desc, .im8-va-interventions__item-desc').forEach(function(el) {
      el.style.setProperty('color', '#2f0f12', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#2f0f12', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.Index_object_tab_wellness_inneranswers_innerdesktop_innerboxs_innerclose_infos_new, .Index_object_tab_wellness_inneranswers_innerdesktop_innerboxs_innerclose_infos_new *, .Index_object_tab_wellness_inneranswers_innerbutton_new, .Index_object_tab_wellness_inneranswers_innerbutton_new *').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('#cart-icon-bubble, a[href="/cart"]').forEach(function(link) {
      if (link.id === 'cart-icon-bubble') {
        var visibleCountEl = link.querySelector('.cart-count-bubble span[aria-hidden="true"], .cart-count span[aria-hidden="true"], [class*="count"] span[aria-hidden="true"]');
        var hiddenCountEl = link.querySelector('.cart-count-bubble .visually-hidden, .cart-count .visually-hidden');
        if (visibleCountEl && hiddenCountEl) {
          var visibleCount = (visibleCountEl.textContent || '').trim().replace(/\s+/g, ' ');
          var hiddenCount = (hiddenCountEl.textContent || '').trim().replace(/\s+/g, ' ');
          link.setAttribute('aria-label', ('Cart ' + visibleCount + ' ' + hiddenCount).replace(/\s+/g, ' ') + ' - View cart');
          return;
        }
      }
      var visual = (link.textContent || '').replace(/\s+/g, ' ').trim();
      if (visual && /cart/i.test(visual)) {
        link.setAttribute('aria-label', visual.replace(/(\d)(\d\s+items?)/i, '$1 $2') + ' - View cart');
      }
    });


    document.querySelectorAll('#Index_object_table_column_see_more, .Index_object_table_column_see_more').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('background-color', '#f5eaea', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    document.querySelectorAll('.Page_influencer_banner_cta_wrapper .cta-button, .Page_influencer_banner_cta_wrapper a.cta-button').forEach(function(el) {
      el.style.setProperty('color', '#ffffff', 'important');
      el.style.setProperty('background-color', '#7a0012', 'important');
      el.style.setProperty('background', '#7a0012', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });


    if (shouldRecolorComparisonRichText) document.querySelectorAll('.comparison-article-hero__author, .comparison-article-hero__author *, .comparison-article-hero__date').forEach(function(el) {
      el.style.setProperty('color', '#2f0f12', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    if (shouldRecolorComparisonRichText) document.querySelectorAll('.rte .p1, .rte .p1 *, .rte li, .rte li *, .shopify-policy__body .p1, .shopify-policy__body li, .comparison-article-content:not(.im8-premium-compare), .comparison-article-content:not(.im8-premium-compare) *').forEach(function(el) {
      el.style.setProperty('color', '#2f0f12', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function(heading) {
      var text = (heading.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return;
      var img = heading.querySelector('img');
      if (!img) return;
      var alt = (img.getAttribute('alt') || '').replace(/\s+/g, ' ').trim();
      var label = alt && !/^section heading$/i.test(alt) ? alt : 'Section overview';
      // Do not append visible fallback text into image/logo headings; it can
      // overlay brand art. Give the heading an accessible name instead.
      if (!heading.getAttribute('aria-label')) heading.setAttribute('aria-label', label);
    });


    if (shouldRecolorComparisonRichText) document.querySelectorAll('.rte > p, .rte > p *, .rte > span, .rte > strong, .rte > em, .shopify-policy__body p, .shopify-policy__body p *').forEach(function(el) {
      // Keep this scoped to article/policy rich text. A previous broad main p/span
      // selector recolored header/logo overlays and dark hero copy.
      el.style.setProperty('color', '#2f0f12', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    document.querySelectorAll('.Index_object_tab_wellness_inneranswers_innerdesktop_innerboxs_innerclose_new, .Index_object_tab_wellness_inneranswers_innerdesktop_innerboxs_innerclose_new *').forEach(function(el) {
      el.style.setProperty('color', '#3b1a1a', 'important');
      el.style.setProperty('opacity', '1', 'important');
    });

    fixFlavorTriggerRoles();

    // Contact topic dropdown: add a durable accessible name even when theme JS
    // replaces the native select with a custom widget.
    document.querySelectorAll('#topics-select, select[name="contact[topic]"]').forEach(function(select) {
      if (!select.getAttribute('aria-label') && !select.getAttribute('aria-labelledby')) {
        select.setAttribute('aria-label', 'Contact topic');
      }
      if (!select.getAttribute('title')) select.setAttribute('title', 'Contact topic');
    });

    // Empty image-only headings flagged by SortSite: if the image is decorative,
    // hide the heading; otherwise make the image alt supply the name.
    document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function(heading) {
      var text = heading.textContent.trim();
      if (text) return;
      var img = heading.querySelector('img');
      if (!img) return;
      if (!img.getAttribute('alt')) img.setAttribute('alt', 'Section heading');
    });

    // Legacy topbar/logo links built from empty div backgrounds.
    document.querySelectorAll('a[href="https://im8health.com/"], a[href="/"], a[href^="/products/"], a[href^="https://im8health.com/products/"]').forEach(function(link) {
      if (getA11yName(link)) return;
      link.setAttribute('aria-label', readableFromUrl(link.getAttribute('href'), 'Open IM8 link'));
    });
  }

  // ==========================================================================
  // 9. DYNAMIC CONTENT OBSERVER
  // ==========================================================================
  
  function setupDynamicContentObserver() {
    const observer = new MutationObserver(debounce(() => {
      fixImageAltText();
      fixLinkAccessibility();
      fixSameLinkTextDifferentDestinations();
      fixDescriptiveLinkText();
      fixButtonAccessibility();
      fixFormAccessibility();
      fixCarouselAccessibility();
      fixScrollableContent();
      restorePopulatedHeadings();
      fixEmptyHeadings();
      fixIframeTitles();
      fixAriaOnGenericElements();
      restoreAriaHiddenFocusable();
      fixAriaHiddenFocusable();
      fixVisualLabelMismatch();
      fixPriceAccessibility();
      fixSupplementFactsPopup();
      fixFootnoteLinks();
      fixHeadingHierarchy();
      fixSvgAccessibility();
      fixFormAriaAttributes();
      fixConsolidatedLevelA();
      fixPowerMapperScanSpecificIssues();
      injectPowerMapperFinalStyles();
      fixPowerMapperInlineContrast();
      }, 500));
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // ==========================================================================
  // 10. REDUCED MOTION HANDLING
  // ==========================================================================
  
  function handleReducedMotion() {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    
    document.documentElement.classList.add('reduce-motion');
    
    // Pause autoplay swipers
    document.querySelectorAll('.swiper').forEach(el => {
      if (el.swiper && el.swiper.autoplay) {
        el.swiper.autoplay.stop();
      }
    });

    // Pause all auto-playing videos
    document.querySelectorAll('video[autoplay], video[muted]').forEach(function(v) {
      if (v.closest('.Page_about_partner_detail_main_new, .Index_tab_percent_studies_main_new, .im8-clinical-proof, .statistics-grid')) return;
      v.pause();
      v.removeAttribute('autoplay');
    });
  }

  // ==========================================================================
  // 11. VIEWPORT ZOOM FIX - Remove zoom restrictions for accessibility
  // ==========================================================================
  
  function fixViewportZoom() {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;
    
    let content = viewport.getAttribute('content') || '';
    
    // Check if zoom is restricted
    if (content.includes('maximum-scale') || content.includes('user-scalable')) {
      // Remove zoom restrictions
      content = content
        .replace(/,?\s*maximum-scale\s*=\s*[\d.]+/gi, '')
        .replace(/,?\s*user-scalable\s*=\s*(no|0)/gi, '')
        .replace(/,\s*,/g, ',')
        .replace(/^,\s*/, '')
        .replace(/,\s*$/, '')
        .trim();
      
      viewport.setAttribute('content', content);
    }
  }

  // ==========================================================================
  // 17. CHATBOT CONSENT DISCLOSURE (Dentons Requirement #2)
  // --------------------------------------------------------------------------
  // REMOVED: Custom JS intercept replaced by Gorgias's built-in
  // "Display Privacy Disclosure" setting.
  // Configure in: Gorgias Admin → Settings → Channels → Chat → Preferences
  // → Toggle ON "Display Privacy Disclosure"
  // See: https://docs.gorgias.com/en-US/gorgias-chat-privacy-disclosure-and-consent-361578
  // ==========================================================================

  // ==========================================================================
  // 18. PRICE ACCESSIBILITY — Sale vs Regular (Douglass v. IM8 Issue A)
  // ==========================================================================

  /**
   * Ensures screen readers clearly differentiate sale price from regular price.
   * The theme already has visually-hidden labels but both price__regular and
   * price__sale exist in the DOM simultaneously. This ensures the hidden one
   * has aria-hidden so screen readers don't read both.
   */
  function fixPriceAccessibility() {
    document.querySelectorAll('.price--on-sale').forEach(function(priceContainer) {
      var regular = priceContainer.querySelector('.price__regular');
      var sale = priceContainer.querySelector('.price__sale');
      if (regular && sale) {
        regular.setAttribute('aria-hidden', 'true');
      }
    });
    // Ensure strikethrough prices are announced properly
    document.querySelectorAll('.price__sale s, .price__sale del, .price__sale strike').forEach(function(el) {
      if (!el.closest('[aria-hidden="true"]') && !el.getAttribute('aria-label')) {
        var priceText = el.textContent.trim();
        if (priceText) {
          el.setAttribute('aria-label', 'Regular price ' + priceText);
        }
      }
    });
  }

  // ==========================================================================
  // 19. SUPPLEMENT FACTS POPUP ACCESSIBILITY (Douglass v. IM8 Issue D)
  // ==========================================================================

  /**
   * Fixes the supplement facts popup so screen readers can navigate and read
   * its contents. Adds dialog role, focus management, and proper labeling.
   */
  function fixSupplementFactsPopup() {
    document.querySelectorAll('.Meta_supplement_popup_mainpopup_new').forEach(function(popup) {
      if (popup.dataset.a11yPopupFixed) return;
      popup.dataset.a11yPopupFixed = 'true';

      // Add dialog semantics
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      popup.setAttribute('aria-label', 'Supplement Facts');

      // Fix the close button
      var closeBtn = popup.querySelector('.close_Meta_supplement_popup');
      if (closeBtn) {
        closeBtn.setAttribute('role', 'button');
        closeBtn.setAttribute('aria-label', 'Close supplement facts');
        closeBtn.setAttribute('tabindex', '0');
        // Allow keyboard activation
        closeBtn.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            closeBtn.click();
          }
        });
      }

      // Add alt text to supplement facts images
      popup.querySelectorAll('img:not([alt]), img[alt=""]').forEach(function(img) {
        img.setAttribute('alt', 'Supplement Facts label');
      });

      // Make the scrollable content area focusable so SR can navigate it
      var scrollArea = popup.querySelector('.Meta_supplement_popup_innerpopup_new');
      if (scrollArea) {
        scrollArea.setAttribute('tabindex', '0');
        scrollArea.setAttribute('role', 'document');
        scrollArea.setAttribute('aria-label', 'Supplement Facts details - scroll to read');
      }
    });

    // Intercept popup open to manage focus
    document.querySelectorAll('.open_Meta_supplement_popup').forEach(function(openBtn) {
      if (openBtn.dataset.a11yOpenFixed) return;
      openBtn.dataset.a11yOpenFixed = 'true';

      openBtn.addEventListener('click', function() {
        setTimeout(function() {
          var popup = document.querySelector('.Meta_supplement_popup_mainpopup_new');
          if (popup && popup.offsetParent !== null) {
            // Move focus into the popup
            var scrollArea = popup.querySelector('.Meta_supplement_popup_innerpopup_new');
            if (scrollArea) {
              scrollArea.focus();
            }
            announce('Supplement Facts dialog opened');

            // Trap focus inside the popup
            popup.addEventListener('keydown', function trapFocus(e) {
              if (e.key === 'Escape') {
                e.preventDefault();
                var closeBtn = popup.querySelector('.close_Meta_supplement_popup');
                if (closeBtn) closeBtn.click();
                popup.removeEventListener('keydown', trapFocus);
                return;
              }
              if (e.key === 'Tab') {
                var focusable = popup.querySelectorAll('[tabindex], button, a[href], input, [role="button"]');
                var first = focusable[0];
                var last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault();
                  last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault();
                  first.focus();
                }
              }
            });
          }
        }, 300);
      });
    });
  }

  // ==========================================================================
  // 20. FOOTNOTE ACCESSIBILITY (Douglass v. IM8 Issue C)
  // ==========================================================================

  /**
   * Links footnote reference asterisks to their corresponding disclaimer text.
   *
   * Three-part approach:
   * 1. Assign anchor IDs to footer disclaimer containers so they can be targets.
   * 2. Find asterisk references: .small-asterisk elements, standalone span/sup
   *    with only asterisk text, and leading asterisks in disclaimer paragraphs.
   * 3. Wrap leading asterisks in disclaimer text (e.g., "*These statements...")
   *    as anchor targets with proper IDs so references can link to them.
   */
  function fixFootnoteLinks() {
    // --- Step 1: ID the footer disclaimer containers ---
    var singleStarDisclaimer = null;
    var doubleStarDisclaimer = null;

    // Footer banner claim containers
    var claimContainer = document.querySelector('.Footer_2_sides_banner_claim_content');
    var claimContainer2 = document.querySelector('.Footer_2_sides_banner_claim_content_2');

    if (claimContainer && !claimContainer.id) {
      claimContainer.id = 'footnote-fda-disclaimer';
      singleStarDisclaimer = claimContainer;
    } else if (claimContainer) {
      singleStarDisclaimer = claimContainer;
    }

    if (claimContainer2 && !claimContainer2.id) {
      claimContainer2.id = 'footnote-welcome-kit';
      doubleStarDisclaimer = claimContainer2;
    } else if (claimContainer2) {
      doubleStarDisclaimer = claimContainer2;
    }

    // Also ID any other disclaimer elements
    var disclaimerSel =
      '.Index_object_table_column_footer_disclaimer, [class*="disclaimer"], [class*="footnote"]:not(.footnote-marker):not(.footnote-ref)';
    var otherDisclaimers = document.querySelectorAll(disclaimerSel);
    otherDisclaimers.forEach(function(d, i) {
      if (!d.id) {
        var id = 'footnote-disclaimer-' + i;
        while (document.getElementById(id)) {
          id = 'footnote-disclaimer-' + i + '-' + Math.random().toString(36).substr(2, 4);
        }
        d.id = id;
      }
    });

    // Build a fallback disclaimer lookup: * -> single, ** -> double, else first available
    function getDisclaimerForAsterisks(asteriskCount, nearSection) {
      if (asteriskCount === 2 && doubleStarDisclaimer) return doubleStarDisclaimer;
      if (asteriskCount === 1 && singleStarDisclaimer) return singleStarDisclaimer;
      // Try section-local disclaimer
      if (nearSection) {
        var local = nearSection.querySelector(disclaimerSel);
        if (local) return local;
      }
      // Fallback
      if (singleStarDisclaimer) return singleStarDisclaimer;
      if (otherDisclaimers.length > 0) return otherDisclaimers[0];
      return null;
    }

    // --- Step 2: Find and link asterisk references ---
    // 2a. .small-asterisk elements (in product benefits headings)
    document.querySelectorAll('.small-asterisk').forEach(function(el) {
      if (el.dataset.a11yFootnoteFixed) return;
      el.dataset.a11yFootnoteFixed = 'true';
      var count = (el.textContent.match(/\*/g) || []).length;
      var section = el.closest('section, [class*="main_new"], [class*="section"], [class*="product"]');
      var disclaimer = getDisclaimerForAsterisks(count, section);
      if (disclaimer) {
        var link = document.createElement('a');
        link.href = '#' + disclaimer.id;
        link.setAttribute('aria-label', count > 1 ? 'See second footnote disclaimer' : 'See footnote disclaimer');
        link.className = 'footnote-ref';
        link.style.cssText = 'text-decoration:none;color:inherit;';
        link.innerHTML = el.innerHTML;
        el.innerHTML = '';
        el.appendChild(link);
      }
    });

    // 2b. Standalone span/sup with only asterisk text
    document.querySelectorAll('span, sup').forEach(function(el) {
      if (el.classList.contains('small-asterisk')) return;
      if (el.dataset.a11yFootnoteFixed) return;
      var text = el.textContent.trim();
      if (/^\*{1,3}$/.test(text) && !el.querySelector('a')) {
        var parent = el.parentElement;
        if (parent && parent.closest('main, [role="main"], section, [class*="product"], [class*="benefits"], [class*="object"], footer')) {
          el.dataset.a11yFootnoteFixed = 'true';
          var count = text.length;
          var section = el.closest('section, [class*="main_new"], [class*="section"], [class*="product"]');
          var disclaimer = getDisclaimerForAsterisks(count, section);
          if (disclaimer) {
            var link = document.createElement('a');
            link.href = '#' + disclaimer.id;
            link.setAttribute('aria-label', count > 1 ? 'See second footnote disclaimer' : 'See footnote disclaimer');
            link.className = 'footnote-ref';
            link.style.cssText = 'text-decoration:none;color:inherit;';
            link.innerHTML = el.innerHTML;
            el.innerHTML = '';
            el.appendChild(link);
          }
        }
      }
    });

    // --- Step 3: Wrap leading asterisks in disclaimer paragraphs ---
    // e.g., "*These statements..." -> <a id="...">*</a>These statements...
    // This makes the disclaimer itself an anchor target AND gives the asterisk
    // a role for screen readers.
    // Build list of containers to process: the explicit banner claim containers
    // PLUS any footer element containing text that starts with asterisks
    var disclaimerContainers = [];
    if (claimContainer) disclaimerContainers.push(claimContainer);
    if (claimContainer2) disclaimerContainers.push(claimContainer2);

    // Also catch newsletter disclaimer text in footer
    var newsletterDisclaimer = document.querySelector('.Footer_1_newsletter_texts_new');
    if (newsletterDisclaimer && disclaimerContainers.indexOf(newsletterDisclaimer) === -1) {
      disclaimerContainers.push(newsletterDisclaimer);
    }

    disclaimerContainers.forEach(function(container) {
      if (!container || container.dataset.a11yDisclaimerFixed) return;
      container.dataset.a11yDisclaimerFixed = 'true';

      // Ensure the container has an ID for anchor targeting
      if (!container.id) {
        container.id = 'footnote-disc-' + Math.random().toString(36).substr(2, 6);
      }

      // Find text nodes that start with asterisks and wrap them
      var walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
      var node;
      while (node = walker.nextNode()) {
        var text = node.textContent;
        var match = text.match(/^(\s*)(\*{1,3})/);
        if (match) {
          var leading = match[1];
          var asterisks = match[2];
          var rest = text.substring(leading.length + asterisks.length);
          var noteSpan = document.createElement('span');
          noteSpan.setAttribute('role', 'note');
          noteSpan.setAttribute('aria-label', asterisks.length > 1 ? 'Double asterisk footnote' : 'Footnote');
          noteSpan.textContent = asterisks;
          noteSpan.className = 'footnote-marker';
          var restNode = document.createTextNode(rest);
          if (leading) {
            node.parentNode.insertBefore(document.createTextNode(leading), node);
          }
          node.parentNode.insertBefore(noteSpan, node);
          node.parentNode.insertBefore(restNode, node);
          node.parentNode.removeChild(node);
          break;
        }
      }
    });
  }

  // ==========================================================================
  // 21. CHAT BUTTON EARLY ANNOUNCEMENT (Douglass v. IM8 Issue B)
  // ==========================================================================

  /**
   * Adds a screen-reader-only announcement when Gorgias chat loads,
   * so users are aware of chat support early — not just at the end of DOM.
   */
  function setupChatAnnouncement() {
    var announced = false;
    var observer = new MutationObserver(function() {
      if (announced) return;
      var chatContainer = document.querySelector('#gorgias-chat-container, div[id*="gorgias-chat"], iframe[id*="gorgias-chat"]');
      if (chatContainer) {
        announced = true;
        // Label the chat widget properly
        chatContainer.setAttribute('role', 'complementary');
        chatContainer.setAttribute('aria-label', 'Chat support');
        announce('Chat support is available');
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    // Stop watching after 30s
    setTimeout(function() { observer.disconnect(); }, 30000);
  }

  // ==========================================================================
  // 22. HEADING HIERARCHY FIX (WCAG 1.3.1 Level A)
  // ==========================================================================

  /**
   * Walks all visible headings in DOM order and fixes skipped levels.
   * Uses role="heading" + aria-level to override the announced level
   * without changing the visual tag (h1-h6 CSS still applies).
   */
  function fixHeadingHierarchy() {
    var headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    var expectedMax = 1; // first heading sets the baseline

    headings.forEach(function(h) {
      // Skip headings we've hidden (empty headings)
      if (h.getAttribute('aria-hidden') === 'true') return;
      // Skip headings already fixed
      if (h.dataset.a11yHeadingFixed) return;

      var level = parseInt(h.tagName[1]);

      if (level > expectedMax + 1) {
        // Skipped level detected — fix it
        var corrected = expectedMax + 1;
        // Native h1-h6 elements already expose heading semantics. Avoid adding
        // role=heading/aria-level because W3C flags it as redundant.
        h.dataset.a11yHeadingFixed = 'true';
        expectedMax = corrected;
      } else {
        expectedMax = level;
      }
    });
  }

  // ==========================================================================
  // 23. SVG ACCESSIBILITY (WCAG 1.1.1 Level A)
  // ==========================================================================

  /**
   * Ensures all SVGs are either labeled (informational) or hidden (decorative).
   * Bulk-hides unlabeled SVGs since they are overwhelmingly decorative icons.
   */
  function fixSvgAccessibility() {
    document.querySelectorAll('svg').forEach(function(svg) {
      if (svg.dataset.a11ySvgFixed) return;
      // Already has accessible label — skip
      if (svg.getAttribute('aria-label')) return;
      if (svg.getAttribute('aria-labelledby')) return;
      if (svg.getAttribute('role') === 'img' && svg.querySelector('title')) return;
      // Already explicitly hidden — skip
      if (svg.getAttribute('aria-hidden') === 'true') return;

      // Mark as decorative
      svg.setAttribute('aria-hidden', 'true');
      svg.dataset.a11ySvgFixed = 'true';
    });
  }

  // ==========================================================================
  // 24. FORM ARIA-REQUIRED + ARIA-DESCRIBEDBY (WCAG 1.3.1, 3.3.2)
  // ==========================================================================

  /**
   * - Adds aria-required="true" to inputs with native required attribute
   * - Links error/validation messages to inputs via aria-describedby
   */
  function fixFormAriaAttributes() {
    // aria-required on all required inputs
    document.querySelectorAll('[required]:not([aria-required])').forEach(function(el) {
      el.setAttribute('aria-required', 'true');
    });

    // aria-describedby: link error messages to their inputs
    var errorSelectors = [
      '.field__message',
      '.form__message',
      '[class*="error-message"]',
      '[class*="validation-message"]',
      '[class*="field-error"]',
      '.invalid-feedback'
    ];
    document.querySelectorAll(errorSelectors.join(', ')).forEach(function(errEl) {
      if (errEl.dataset.a11yDescFixed) return;
      errEl.dataset.a11yDescFixed = 'true';

      // Find the associated input — look for sibling or parent-child relationship
      var field = errEl.closest('.field, .form-group, .form__group, [class*="field"]');
      var input = field
        ? field.querySelector('input, textarea, select')
        : errEl.previousElementSibling;

      if (input && input.matches && input.matches('input, textarea, select')) {
        if (!errEl.id) {
          errEl.id = 'a11y-err-' + Math.random().toString(36).substr(2, 6);
        }
        var existing = input.getAttribute('aria-describedby') || '';
        if (existing.indexOf(errEl.id) === -1) {
          input.setAttribute('aria-describedby', (existing + ' ' + errEl.id).trim());
        }
      }
    });
  }

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  function init() {
    fixViewportZoom();
    fixPowerMapperValidationIssues();
    injectPowerMapperFinalStyles();
    fixFlavorTriggerRoles();
    createAnnouncer();
    fixImageAltText();
    fixLinkAccessibility();
    fixSameLinkTextDifferentDestinations();
    fixDescriptiveLinkText();
    fixAutoplayVideos();
    fixEmptyHeadings();
    fixClickKeyboardParity();
    fixAriaHiddenFocusable();
    fixVisualLabelMismatch();
    fixIframeTitles();
    fixButtonAccessibility();
    fixFormAccessibility();
    fixLandmarkRoles();
    fixCarouselAccessibility();
    fixScrollableContent();
    fixAriaOnGenericElements();
    fixPriceAccessibility();
    fixSupplementFactsPopup();
    fixFootnoteLinks();
    fixHeadingHierarchy();
    fixSvgAccessibility();
    fixFormAriaAttributes();
    fixConsolidatedLevelA();
    fixPowerMapperScanSpecificIssues();
    setupKeyboardNavigation();
    handleReducedMotion();
    setupDynamicContentObserver();
    setupChatAnnouncement();

    [250, 1000, 2500].forEach(function(delay) {
      setTimeout(fixConsolidatedLevelA, delay);
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run after full page load for lazy content
  window.addEventListener('load', () => {
    setTimeout(() => {
      fixImageAltText();
      fixLinkAccessibility();
      fixSameLinkTextDifferentDestinations();
      fixDescriptiveLinkText();
      fixButtonAccessibility();
      fixAutoplayVideos();
      fixIframeTitles();
      fixAriaOnGenericElements();
      fixHeadingHierarchy();
      fixSvgAccessibility();
      fixFormAriaAttributes();
      fixFootnoteLinks();
      fixConsolidatedLevelA();
      fixPowerMapperScanSpecificIssues();
      injectPowerMapperFinalStyles();
      fixPowerMapperInlineContrast();
      }, 1000);
  });

  // Expose for manual use
  window.IM8Accessibility = {
    init, announce, fixImageAltText, fixLinkAccessibility,
    fixSameLinkTextDifferentDestinations, fixDescriptiveLinkText,
    fixAutoplayVideos, fixIframeTitles, fixAriaOnGenericElements,
    fixConsolidatedLevelA, fixPowerMapperScanSpecificIssues,
    fixTargetBlankWarnings, injectPowerMapperFinalStyles,
    fixPowerMapperInlineContrast
  };

})();
